import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const DataaaaAnalysis = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState('');
  const [analysisMode, setAnalysisMode] = useState('global');
  const [pageView, setPageView] = useState('analysis');
  const [dataFiles, setDataFiles] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [modelType, setModelType] = useState('autoencoder');

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get('/api/analysis-files');
        const files = res.data?.files || [];
        setDataFiles(files);
        if (files.length > 0) {
          setSelectedSource(res.data?.default || files[0].id);
        }
      } catch {
        setDataFiles([]);
      }
    };
    fetchFiles();
  }, []);
  const isLstm = modelType === 'lstm';
  const modelLabel = isLstm ? 'LSTM' : 'Feedforward';
  /** Primary window length (both models). */
  const SEQUENCE_LENGTH = 5;
  /** Comparison sequence lengths per model family. */
  const COMPARISON_SEQUENCE_LENGTH = isLstm ? 8 : 7;
  const EXTENDED_COMPARISON_SEQUENCE_LENGTH = isLstm ? 11 : 10;
  const LARGE_COMPARISON_SEQUENCE_LENGTH = isLstm ? 48 : 47;
  /** Feedforward only: wider bottleneck for 11- and 48-element windows (input too small for seq 5/8). */
  const FEEDFORWARD_LARGE_BOTTLENECK = 8;
  const FEEDFORWARD_DEEP_LAYERS = 4;
  /** LSTM encoder/decoder hidden state size (bottleneck). */
  const LSTM_BOTTLENECK = 8;
  const feedforwardBottleneckParams = (sequenceLength) => (
    !isLstm
      && (sequenceLength === EXTENDED_COMPARISON_SEQUENCE_LENGTH
        || sequenceLength === LARGE_COMPARISON_SEQUENCE_LENGTH)
      ? {
        bottleneck_dim: FEEDFORWARD_LARGE_BOTTLENECK,
        num_layers: FEEDFORWARD_DEEP_LAYERS,
      }
      : {}
  );

  const loadData = async () => {
    if (!selectedSource) {
      setError('Select a data file first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // B27 and other large CSVs: allow much longer per training job.
      const isLargeSource = selectedSource === 'b27' || selectedSource === 'm16';
      const feedforwardTimeoutMs = isLargeSource ? 900000 : 300000;
      const lstmTimeoutMs = isLargeSource ? 1800000 : 600000;
      const selectedModelTimeoutMs = isLstm ? lstmTimeoutMs : feedforwardTimeoutMs;
      const axiosOpts = (timeoutMs) => ({
        timeout: timeoutMs,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      const fileParams = {
        source: selectedSource,
        model_type: modelType,
        ...(isLstm ? { bottleneck_dim: LSTM_BOTTLENECK } : {}),
      };
      const sharedFileParams = { source: selectedSource, analysis_mode: analysisMode };
      const dataaaaGet = (timeoutMs, params) => axios.get('/api/dataaaa-analysis', {
        ...axiosOpts(timeoutMs),
        params,
      });

      // Wave 1: selected model at 4 sequence lengths (avoid 6 parallel trainings on big files).
      const [
        baseResponse,
        comparisonResponse,
        extendedComparisonResponse,
        largeComparisonResponse,
      ] = await Promise.all([
        dataaaaGet(selectedModelTimeoutMs, {
          ...fileParams,
          ...sharedFileParams,
          sequence_length: SEQUENCE_LENGTH,
          _ts: Date.now(),
        }),
        dataaaaGet(selectedModelTimeoutMs, {
          ...fileParams,
          ...sharedFileParams,
          sequence_length: COMPARISON_SEQUENCE_LENGTH,
          _ts: Date.now() + 1,
        }),
        dataaaaGet(selectedModelTimeoutMs, {
          ...fileParams,
          ...sharedFileParams,
          sequence_length: EXTENDED_COMPARISON_SEQUENCE_LENGTH,
          ...feedforwardBottleneckParams(EXTENDED_COMPARISON_SEQUENCE_LENGTH),
          _ts: Date.now() + 2,
        }),
        dataaaaGet(selectedModelTimeoutMs, {
          ...fileParams,
          ...sharedFileParams,
          sequence_length: LARGE_COMPARISON_SEQUENCE_LENGTH,
          ...feedforwardBottleneckParams(LARGE_COMPARISON_SEQUENCE_LENGTH),
          _ts: Date.now() + 3,
        }),
      ]);

      // Wave 2: input vs output overlay (Feedforward + LSTM seq=5).
      const skipFfSeq5 = !isLstm && modelType === 'autoencoder';
      const skipLstmSeq5 = isLstm;
      const [feedforwardSeq5Response, lstmSeq5Response] = await Promise.all([
        skipFfSeq5
          ? Promise.resolve(baseResponse)
          : dataaaaGet(feedforwardTimeoutMs, {
            ...sharedFileParams,
            model_type: 'autoencoder',
            sequence_length: SEQUENCE_LENGTH,
            _ts: Date.now() + 4,
          }),
        skipLstmSeq5
          ? Promise.resolve(baseResponse)
          : dataaaaGet(lstmTimeoutMs, {
            ...sharedFileParams,
            model_type: 'lstm',
            sequence_length: SEQUENCE_LENGTH,
            bottleneck_dim: LSTM_BOTTLENECK,
            _ts: Date.now() + 5,
          }),
      ]);

      if (baseResponse.data.error) {
        setError(baseResponse.data.error);
      } else if (comparisonResponse.data.error) {
        setError(comparisonResponse.data.error);
      } else if (extendedComparisonResponse.data.error) {
        setError(extendedComparisonResponse.data.error);
      } else if (largeComparisonResponse.data.error) {
        setError(largeComparisonResponse.data.error);
      } else {
        const comparisonBySensor = new Map(
          (comparisonResponse.data.sensors || []).map((sensor) => [sensor.sensor, sensor])
        );
        const extendedComparisonBySensor = new Map(
          (extendedComparisonResponse.data.sensors || []).map((sensor) => [sensor.sensor, sensor])
        );
        const largeComparisonBySensor = new Map(
          (largeComparisonResponse.data.sensors || []).map((sensor) => [sensor.sensor, sensor])
        );
        const feedforwardSeq5BySensor = new Map(
          (feedforwardSeq5Response.data.sensors || []).map((sensor) => [sensor.sensor, sensor])
        );
        const lstmSeq5BySensor = new Map(
          (lstmSeq5Response.data.sensors || []).map((sensor) => [sensor.sensor, sensor])
        );
        const mergedSensors = (baseResponse.data.sensors || []).map((sensor) => {
          const comparisonSensor = comparisonBySensor.get(sensor.sensor);
          const extendedComparisonSensor = extendedComparisonBySensor.get(sensor.sensor);
          const largeComparisonSensor = largeComparisonBySensor.get(sensor.sensor);
          const ffSeq5Sensor = feedforwardSeq5BySensor.get(sensor.sensor);
          const lstmSeq5Sensor = lstmSeq5BySensor.get(sensor.sensor);
          return {
            ...sensor,
            autoencoder_80_20_compare: comparisonSensor?.autoencoder_80_20 || null,
            autoencoder_80_20_compare_11: extendedComparisonSensor?.autoencoder_80_20 || null,
            autoencoder_80_20_compare_48: largeComparisonSensor?.autoencoder_80_20 || null,
            autoencoder_80_20_feedforward_seq5: ffSeq5Sensor?.autoencoder_80_20 || null,
            autoencoder_80_20_lstm_seq5: lstmSeq5Sensor?.autoencoder_80_20 || null,
          };
        });
        setResults({
          ...baseResponse.data,
          model_type: modelType,
          sequence_lengths_compared: [
            SEQUENCE_LENGTH,
            COMPARISON_SEQUENCE_LENGTH,
            EXTENDED_COMPARISON_SEQUENCE_LENGTH,
            LARGE_COMPARISON_SEQUENCE_LENGTH
          ],
          sensors: mergedSensors
        });
        if (mergedSensors.length > 0) {
          setSelectedSensor(mergedSensors[0].sensor);
        }
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        const large = selectedSource === 'b27' || selectedSource === 'm16';
        const mins = Math.round(
          (isLstm
            ? (large ? 1800000 : 600000)
            : (large ? 900000 : 300000)) / 60000
        );
        setError(
          `Request timed out after ${mins} minutes (limit for ${large ? 'B27/large files' : 'this file'}). `
          + 'Large files train many sensors — wait and try again, use Feedforward only, or a smaller dataset.'
        );
      } else if (err.request) {
        setError(
          'Cannot reach the backend. Start it with: python3 backend/app.py (must listen on port 5001). '
          + 'Then use the app at http://localhost:3000 (npm start), not the build/ folder directly.'
        );
      } else {
        setError(err.message || 'Failed to load analysis data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sensorData = results?.sensors?.find((s) => s.sensor === selectedSensor);
  const strainSeriesForAutocorr = sensorData?.strain_zeroed?.length
    ? sensorData.strain_zeroed
    : (sensorData?.strain_raw || []);

  const computeAutocorrelation = (series, maxLag = 200) => {
    if (!Array.isArray(series) || series.length < 3) {
      return { lags: [], values: [], variance: 0 };
    }

    const clean = series
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));

    if (clean.length < 3) {
      return { lags: [], values: [], variance: 0 };
    }

    const n = clean.length;
    const mean = clean.reduce((acc, v) => acc + v, 0) / n;
    const centered = clean.map((v) => v - mean);
    const variance = centered.reduce((acc, v) => acc + v * v, 0) / n;
    if (!Number.isFinite(variance) || variance <= 1e-12) {
      return { lags: [], values: [], variance };
    }

    const cappedLag = Math.max(1, Math.min(maxLag, n - 1));
    const lags = [];
    const values = [];

    for (let lag = 0; lag <= cappedLag; lag += 1) {
      let num = 0;
      const pairs = n - lag;
      for (let i = 0; i < pairs; i += 1) {
        num += centered[i] * centered[i + lag];
      }
      const cov = num / pairs;
      lags.push(lag);
      values.push(cov / variance);
    }

    return { lags, values, variance };
  };

  const estimateMedianStepSeconds = (timestampStrings) => {
    if (!Array.isArray(timestampStrings) || timestampStrings.length < 3) return null;
    const times = timestampStrings
      .map((ts) => new Date(ts).getTime())
      .filter((ms) => Number.isFinite(ms))
      .sort((a, b) => a - b);
    if (times.length < 3) return null;

    const diffs = [];
    for (let i = 1; i < times.length; i += 1) {
      const d = (times[i] - times[i - 1]) / 1000;
      if (Number.isFinite(d) && d > 0) diffs.push(d);
    }
    if (diffs.length === 0) return null;
    diffs.sort((a, b) => a - b);
    return diffs[Math.floor(diffs.length / 2)];
  };

  const autocorr = computeAutocorrelation(strainSeriesForAutocorr, 200);
  const stepSeconds = estimateMedianStepSeconds(sensorData?.timestamps || []);
  const lagAtThreshold = (threshold) => {
    const idx = autocorr.values.findIndex((v, i) => i > 0 && v < threshold);
    if (idx === -1) return null;
    const lag = autocorr.lags[idx];
    const approxHours = stepSeconds ? (lag * stepSeconds) / 3600 : null;
    return { lag, approxHours };
  };
  const lag05 = lagAtThreshold(0.5);
  const lag02 = lagAtThreshold(0.2);
  const lag0 = lagAtThreshold(0.0);

  const autocorrelationPlot = (sensorData && autocorr.lags.length > 0) ? [
    {
      x: autocorr.lags,
      y: autocorr.values,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Autocorrelation',
      line: { color: '#1565c0', width: 2 },
      marker: { size: 4, color: '#1565c0' }
    },
    {
      x: [0, autocorr.lags[autocorr.lags.length - 1]],
      y: [0.5, 0.5],
      type: 'scatter',
      mode: 'lines',
      name: 'r = 0.5',
      line: { color: '#f9a825', width: 1.5, dash: 'dash' }
    },
    {
      x: [0, autocorr.lags[autocorr.lags.length - 1]],
      y: [0.2, 0.2],
      type: 'scatter',
      mode: 'lines',
      name: 'r = 0.2',
      line: { color: '#ef6c00', width: 1.5, dash: 'dot' }
    },
    {
      x: [0, autocorr.lags[autocorr.lags.length - 1]],
      y: [0, 0],
      type: 'scatter',
      mode: 'lines',
      name: 'r = 0',
      line: { color: '#9e9e9e', width: 1.5, dash: 'dash' }
    }
  ] : [];

  const strainVsTempPlot = sensorData ? [{
    x: sensorData.temperature,
    y: sensorData.strain_raw,
    type: 'scatter',
    mode: 'markers',
    name: `${sensorData.sensor} vs ${sensorData.temperature_sensor}`,
    marker: { color: '#1f77b4', size: 6, opacity: 0.8 }
  }] : [];

  const ae = sensorData?.autoencoder_80_20;
  const aeFeedforwardSeq5 = sensorData?.autoencoder_80_20_feedforward_seq5;
  const aeLstmSeq5 = sensorData?.autoencoder_80_20_lstm_seq5;
  const pickTestOriginalStrainModel = (...models) => models.find(
    (m) => m?.status === 'ok'
      && m?.test_timestamps?.length > 0
      && m?.test_original_strain_aligned?.length === m.test_timestamps.length
  );
  const testOriginalStrainModel = pickTestOriginalStrainModel(
    aeFeedforwardSeq5,
    aeLstmSeq5,
    ae
  );

  const originalStrainVsTimestampPlot = testOriginalStrainModel ? [
    {
      x: testOriginalStrainModel.test_timestamps,
      y: testOriginalStrainModel.test_original_strain_aligned,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Original Strain (Test)',
      line: { color: '#2ca02c', width: 2 },
      marker: { size: 5 }
    }
  ] : [];
  const buildSeq5ReconstructedStrainTrace = (ae, label, color, dash = 'dash') => {
    if (!ae || ae.status !== 'ok' || !ae.test_timestamps?.length
      || ae.test_reconstructed_strain?.length !== ae.test_timestamps.length) {
      return null;
    }
    return {
      x: ae.test_timestamps,
      y: ae.test_reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: label,
      line: { color, width: 2, dash },
    };
  };
  const seq5InputModel = pickTestOriginalStrainModel(aeFeedforwardSeq5, aeLstmSeq5, ae);
  const combinedStrainReconstructionPlot = sensorData
    ? [
      seq5InputModel ? {
        x: seq5InputModel.test_timestamps,
        y: seq5InputModel.test_original_strain_aligned,
        type: 'scatter',
        mode: 'lines',
        name: 'Input (test strain)',
        line: { color: '#2ca02c', width: 2.5 },
      } : null,
      buildSeq5ReconstructedStrainTrace(
        aeFeedforwardSeq5,
        'Feedforward output',
        '#1f77b4',
        'dash'
      ),
      buildSeq5ReconstructedStrainTrace(
        aeLstmSeq5,
        'LSTM output',
        '#ff7f0e',
        'dot'
      ),
    ].filter(Boolean)
    : [];
  const hasCombinedOverlayPlot = combinedStrainReconstructionPlot.length >= 3;

  const zeroedStrainVsTimestampPlot = sensorData ? [
    {
      x: sensorData.timestamps,
      y: sensorData.strain_zeroed,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Zeroed Strain',
      line: { color: '#d62728', width: 2, dash: 'dash' },
      marker: { size: 5 }
    }
  ] : [];

  const aeCompare = sensorData?.autoencoder_80_20_compare;
  const aeCompare11 = sensorData?.autoencoder_80_20_compare_11;
  const aeCompare48 = sensorData?.autoencoder_80_20_compare_48;
  const compareSeq8 = aeCompare?.sequence_length ?? COMPARISON_SEQUENCE_LENGTH;
  const compareSeq11 = aeCompare11?.sequence_length ?? EXTENDED_COMPARISON_SEQUENCE_LENGTH;
  const compareSeq48 = aeCompare48?.sequence_length ?? LARGE_COMPARISON_SEQUENCE_LENGTH;
  const windowDesc = (seq) => (
    isLstm
      ? `${seq} timesteps (strain + temp each)`
      : `${seq} strain readings`
  );
  const fmtThreshold = (v) => (Number.isFinite(v) ? Number(v).toFixed(6) : 'N/A');
  const evalMetrics = ae?.evaluation || {};
  const trainStats = evalMetrics.train_score_stats || {};
  const testStats = evalMetrics.test_score_stats || {};
  const baseThresholdValue = Number.isFinite(ae?.base_threshold) ? Number(ae.base_threshold) : null;
  const warningThresholdValue = Number.isFinite(ae?.scaled_threshold) ? Number(ae.scaled_threshold) : null;
  const criticalThresholdValue = Number.isFinite(ae?.critical_threshold)
    ? Number(ae.critical_threshold)
    : (Number.isFinite(ae?.threshold) ? Number(ae.threshold) : null);
  const trainMeanValue = Number.isFinite(trainStats?.mean) ? Number(trainStats.mean) : null;
  const trainStdValue = Number.isFinite(trainStats?.std) ? Number(trainStats.std) : null;
  const impliedK = (threshold) => {
    if (!Number.isFinite(threshold) || !Number.isFinite(trainMeanValue) || !Number.isFinite(trainStdValue) || trainStdValue === 0) {
      return null;
    }
    return (threshold - trainMeanValue) / trainStdValue;
  };
  const baseImpliedK = impliedK(baseThresholdValue);
  const warningImpliedK = impliedK(warningThresholdValue);
  const criticalImpliedK = impliedK(criticalThresholdValue);
  const scoreCount = ae?.test_scores?.length || 0;
  const anomalyCount = ae?.is_anomaly?.filter(Boolean)?.length || 0;
  const anomalyRate = scoreCount > 0 ? (anomalyCount / scoreCount) * 100 : 0;
  const scoreMean = scoreCount > 0 ? ae.test_scores.reduce((acc, v) => acc + v, 0) / scoreCount : null;
  const scoreStd = scoreCount > 1
    ? Math.sqrt(ae.test_scores.reduce((acc, v) => acc + Math.pow(v - scoreMean, 2), 0) / (scoreCount - 1))
    : null;
  const scoreMin = scoreCount > 0 ? Math.min(...ae.test_scores) : null;
  const scoreMax = scoreCount > 0 ? Math.max(...ae.test_scores) : null;
  const severityFromScore = (score, threshold) => {
    if (threshold == null || score == null || score <= threshold) return 'normal';
    return score > threshold * 1.4 ? 'critical' : 'warning';
  };
  const thresholdSeries = (ae?.adaptive_thresholds && ae.adaptive_thresholds.length === ae?.test_scores?.length)
    ? ae.adaptive_thresholds
    : (ae?.test_scores || []).map(() => ae?.threshold);
  const rawFlags = ae?.is_anomaly_raw?.length ? ae.is_anomaly_raw : (ae?.is_anomaly || []);
  const persistentFlags = ae?.mode === 'global' ? (ae?.is_anomaly_persistent || []) : [];
  const autoencoderPlot = (sensorData && ae?.status === 'ok') ? [
    {
      x: ae.test_timestamps,
      y: ae.test_scores,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Reconstruction Error',
      line: { color: '#1f77b4', width: 2 },
      marker: {
        size: 5,
        color: ae.test_scores.map((score, idx) => {
          if (!rawFlags[idx]) return '#1f77b4';
          const severity = severityFromScore(score, thresholdSeries[idx]);
          return severity === 'critical' ? '#d62728' : '#fbc02d';
        })
      }
    },
    {
      x: ae.test_timestamps,
      y: thresholdSeries,
      type: 'scatter',
      mode: 'lines',
      name: `Threshold (${(ae.threshold || 0).toFixed(6)})`,
      line: { color: '#ff0000', width: 3, dash: 'dash' }
      
    }
  ] : [];
  const compareThresholdSeries = (aeCompare?.adaptive_thresholds && aeCompare.adaptive_thresholds.length === aeCompare?.test_scores?.length)
    ? aeCompare.adaptive_thresholds
    : (aeCompare?.test_scores || []).map(() => aeCompare?.threshold);
  const compareRawFlags = aeCompare?.is_anomaly_raw?.length
    ? aeCompare.is_anomaly_raw
    : (aeCompare?.is_anomaly || []);
  const autoencoderPlot8 = (sensorData && aeCompare?.status === 'ok') ? [
    {
      x: aeCompare.test_timestamps,
      y: aeCompare.test_scores,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Reconstruction Error',
      line: { color: '#1f77b4', width: 2 },
      marker: {
        size: 5,
        color: aeCompare.test_scores.map((score, idx) => {
          if (!compareRawFlags[idx]) return '#1f77b4';
          const severity = severityFromScore(score, compareThresholdSeries[idx]);
          return severity === 'critical' ? '#d62728' : '#fbc02d';
        })
      }
    },
    {
      x: aeCompare.test_timestamps,
      y: compareThresholdSeries,
      type: 'scatter',
      mode: 'lines',
      name: `Threshold (${(aeCompare.threshold || 0).toFixed(6)})`,
      line: { color: '#ff0000', width: 3, dash: 'dash' }
    }
  ] : [];
  const compare11ThresholdSeries = (aeCompare11?.adaptive_thresholds && aeCompare11.adaptive_thresholds.length === aeCompare11?.test_scores?.length)
    ? aeCompare11.adaptive_thresholds
    : (aeCompare11?.test_scores || []).map(() => aeCompare11?.threshold);
  const compare11RawFlags = aeCompare11?.is_anomaly_raw?.length
    ? aeCompare11.is_anomaly_raw
    : (aeCompare11?.is_anomaly || []);
  const autoencoderPlot11 = (sensorData && aeCompare11?.status === 'ok') ? [
    {
      x: aeCompare11.test_timestamps,
      y: aeCompare11.test_scores,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Reconstruction Error',
      line: { color: '#1f77b4', width: 2 },
      marker: {
        size: 5,
        color: aeCompare11.test_scores.map((score, idx) => {
          if (!compare11RawFlags[idx]) return '#1f77b4';
          const severity = severityFromScore(score, compare11ThresholdSeries[idx]);
          return severity === 'critical' ? '#d62728' : '#fbc02d';
        })
      }
    },
    {
      x: aeCompare11.test_timestamps,
      y: compare11ThresholdSeries,
      type: 'scatter',
      mode: 'lines',
      name: `Threshold (${(aeCompare11.threshold || 0).toFixed(6)})`,
      line: { color: '#ff0000', width: 3, dash: 'dash' }
    }
  ] : [];
  const compare48ThresholdSeries = (aeCompare48?.adaptive_thresholds && aeCompare48.adaptive_thresholds.length === aeCompare48?.test_scores?.length)
    ? aeCompare48.adaptive_thresholds
    : (aeCompare48?.test_scores || []).map(() => aeCompare48?.threshold);
  const compare48RawFlags = aeCompare48?.is_anomaly_raw?.length
    ? aeCompare48.is_anomaly_raw
    : (aeCompare48?.is_anomaly || []);
  const autoencoderPlot48 = (sensorData && aeCompare48?.status === 'ok') ? [
    {
      x: aeCompare48.test_timestamps,
      y: aeCompare48.test_scores,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Reconstruction Error',
      line: { color: '#1f77b4', width: 2 },
      marker: {
        size: 5,
        color: aeCompare48.test_scores.map((score, idx) => {
          if (!compare48RawFlags[idx]) return '#1f77b4';
          const severity = severityFromScore(score, compare48ThresholdSeries[idx]);
          return severity === 'critical' ? '#d62728' : '#fbc02d';
        })
      }
    },
    {
      x: aeCompare48.test_timestamps,
      y: compare48ThresholdSeries,
      type: 'scatter',
      mode: 'lines',
      name: `Threshold (${(aeCompare48.threshold || 0).toFixed(6)})`,
      line: { color: '#ff0000', width: 3, dash: 'dash' }
    }
  ] : [];
  const reconstructedSignalPlot = (sensorData && ae?.status === 'ok' && ae?.mode === 'global'
    && ae?.test_timestamps?.length > 0
    && ae?.test_reconstructed_strain?.length === ae?.test_timestamps?.length
    && ae?.test_original_strain_aligned?.length === ae?.test_timestamps?.length) ? [
    {
      x: ae.test_timestamps,
      y: ae.test_original_strain_aligned,
      type: 'scatter',
      mode: 'lines',
      name: 'Original Strain (Test)',
      line: { color: '#1f77b4', width: 2 }
    },
    {
      x: ae.test_timestamps,
      y: ae.test_reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: 'Reconstructed Strain',
      line: { color: '#2ca02c', width: 2, dash: 'dash' }
    }
  ] : [];

  const persistentPlot = (sensorData && ae?.status === 'ok' && ae?.mode === 'global' && persistentFlags.length === ae?.test_scores?.length) ? [
    {
      x: ae.test_timestamps,
      y: ae.test_scores,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Reconstruction Error (Persistence Smoothed)',
      line: { color: '#1f77b4', width: 2 },
      marker: {
        size: 5,
        color: ae.test_scores.map((score, idx) => {
          if (!persistentFlags[idx]) return '#1f77b4';
          const severity = severityFromScore(score, thresholdSeries[idx]);
          return severity === 'critical' ? '#d62728' : '#fbc02d';
        })
      }
    },
    {
      x: ae.test_timestamps,
      y: thresholdSeries,
      type: 'scatter',
      mode: 'lines',
      name: 'Threshold',
      line: { color: '#ff0000', width: 3, dash: 'dash' }
    }
  ] : [];

  const getSeverityPointsForModel = (model) => {
    if (!sensorData || model?.status !== 'ok') return [];
    const modelThresholdSeries = (model?.adaptive_thresholds && model.adaptive_thresholds.length === model?.test_scores?.length)
      ? model.adaptive_thresholds
      : (model?.test_scores || []).map(() => model?.threshold);
    return model.test_scores.map((score, idx) => {
      const threshold = modelThresholdSeries[idx];
      const severity = severityFromScore(score, threshold);
      return {
        timestamp: model.test_timestamps[idx],
        score,
        severity,
        threshold,
        sourceIndex: idx
      };
    }).filter((p) => p.severity !== 'normal');
  };
  const severityPoints = getSeverityPointsForModel(ae);
  const severityPoints8 = getSeverityPointsForModel(aeCompare);
  const severityPoints11 = getSeverityPointsForModel(aeCompare11);
  const severityPoints48 = getSeverityPointsForModel(aeCompare48);
  const anomalyPointGroups = [
    { key: String(SEQUENCE_LENGTH), title: `Anomalies when seq=${SEQUENCE_LENGTH}`, model: ae, points: severityPoints },
    { key: String(compareSeq8), title: `Anomalies when seq=${compareSeq8}`, model: aeCompare, points: severityPoints8 },
    { key: String(compareSeq11), title: `Anomalies when seq=${compareSeq11}`, model: aeCompare11, points: severityPoints11 },
    { key: String(compareSeq48), title: `Anomalies when seq=${compareSeq48}`, model: aeCompare48, points: severityPoints48 }
  ];
  const criticalPoints = (sensorData && ae?.status === 'ok')
    ? (ae?.exact_anomaly_points?.length
      ? ae.exact_anomaly_points.map((pt) => ({
        timestamp: pt.timestamp,
        score: pt.mse_score,
        strain: pt.strain_value,
        isCritical: true
      }))
      : ae.test_scores.map((score, idx) => ({
        timestamp: ae.test_timestamps[idx],
        score,
        strain: ae?.test_original_strain_aligned?.[idx],
        isCritical: ae?.is_alert_critical?.[idx] ?? (severityFromScore(score, thresholdSeries[idx]) === 'critical')
      })).filter((p) => p.isCritical))
    : [];
  const anomalyBeyondThresholdPoints = (sensorData && ae?.status === 'ok')
    ? (ae?.exact_anomaly_points?.length
      ? ae.exact_anomaly_points.map((pt) => ({
        timestamp: pt.timestamp,
        score: pt.mse_score,
        strain: pt.strain_value,
        threshold: pt.threshold_used,
        isBeyond: true
      }))
      : ae.test_scores.map((score, idx) => ({
        timestamp: ae.test_timestamps[idx],
        score,
        strain: ae?.test_original_strain_aligned?.[idx],
        threshold: thresholdSeries[idx],
        isBeyond: score > thresholdSeries[idx]
      })).filter((p) => p.isBeyond))
    : [];

  const normalPdf = (x, mean, std) => {
    if (!Number.isFinite(std) || std <= 0) return 0;
    const z = (x - mean) / std;
    return (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
  };

  const scoreHistogramPlot = (sensorData && ae?.status === 'ok' && ae?.train_scores?.length > 0 && ae?.test_scores?.length > 0)
    ? (() => {
      const trainScores = ae.train_scores;
      const testScores = ae.test_scores;
      const trainMean = trainScores.reduce((a, b) => a + b, 0) / trainScores.length;
      const trainStd = trainScores.length > 1
        ? Math.sqrt(trainScores.reduce((a, b) => a + Math.pow(b - trainMean, 2), 0) / (trainScores.length - 1))
        : 0;
      const trainMin = Math.min(...trainScores);
      const trainMax = Math.max(...trainScores);
      const binWidth = Math.max((trainMax - trainMin) / 30, 1e-6);
      const xStart = trainMin;
      const xEnd = Math.max(trainMax, trainMean + 4 * trainStd);
      const xVals = [];
      for (let x = xStart; x <= xEnd; x += binWidth) xVals.push(x);
      const gaussianCountScaled = xVals.map((x) => normalPdf(x, trainMean, trainStd) * trainScores.length * binWidth);
      const meanPlus2Std = trainMean + (2 * trainStd);

      return [
        {
          x: trainScores,
          type: 'histogram',
          name: 'Train MSE Histogram',
          marker: { color: 'rgba(31, 119, 180, 0.55)' },
          opacity: 0.7,
          autobinx: false,
          xbins: { start: trainMin, end: xEnd, size: binWidth }
        },
        {
          x: testScores,
          type: 'histogram',
          name: 'Test MSE Histogram',
          marker: { color: 'rgba(214, 39, 40, 0.45)' },
          opacity: 0.55,
          autobinx: false,
          xbins: { start: trainMin, end: xEnd, size: binWidth }
        },
        {
          x: xVals,
          y: gaussianCountScaled,
          type: 'scatter',
          mode: 'lines',
          name: 'Gaussian fit on train MSE',
          line: { color: '#2ca02c', width: 3 }
        },
        {
          x: [meanPlus2Std, meanPlus2Std],
          y: [0, Math.max(...gaussianCountScaled, 1)],
          type: 'scatter',
          mode: 'lines',
          name: `Mean + 2*Std (${meanPlus2Std.toFixed(6)})`,
          line: { color: '#ff9800', width: 3, dash: 'dash' }
        }
      ];
    })()
    : [];
  const trainMseHistogramPlot = (sensorData && ae?.status === 'ok' && ae?.train_scores?.length > 0)
    ? (() => {
      const trainScores = ae.train_scores;
      const trainMean = trainScores.reduce((a, b) => a + b, 0) / trainScores.length;
      const trainStd = trainScores.length > 1
        ? Math.sqrt(trainScores.reduce((a, b) => a + Math.pow(b - trainMean, 2), 0) / (trainScores.length - 1))
        : 0;
      const trainMin = Math.min(...trainScores);
      const trainMax = Math.max(...trainScores);
      const binWidth = Math.max((trainMax - trainMin) / 30, 1e-6);
      const xStart = trainMin;
      const xEnd = Math.max(trainMax, trainMean + 4 * trainStd);
      const meanPlus3Std = trainMean + (3 * trainStd);
      const p98 = [...trainScores].sort((a, b) => a - b)[Math.max(0, Math.min(trainScores.length - 1, Math.floor(0.98 * trainScores.length) - 1))];
      const baseThreshold = Math.max(p98, meanPlus3Std);
      const thresholdScale = ae?.threshold_scale ?? 1.15;
      const scaledThreshold = baseThreshold * thresholdScale;
      const critical13Threshold = scaledThreshold * 1.4;
      const xVals = [];
      for (let x = xStart; x <= xEnd; x += binWidth) xVals.push(x);
      const gaussianCountScaled = xVals.map((x) => normalPdf(x, trainMean, trainStd) * trainScores.length * binWidth);
      const yMax = Math.max(...gaussianCountScaled, 1);

      return [
        {
          x: trainScores,
          type: 'histogram',
          name: 'Train MSE',
          marker: { color: '#1f77b4' },
          opacity: 0.8,
          autobinx: false,
          xbins: { start: trainMin, end: xEnd, size: binWidth }
        },
        {
          x: xVals,
          y: gaussianCountScaled,
          type: 'scatter',
          mode: 'lines',
          name: 'Gaussian fit (light)',
          line: { color: 'rgba(46, 125, 50, 0.45)', width: 2 }
        },
        {
          x: [meanPlus3Std, meanPlus3Std],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `Mean + 3*Std (${meanPlus3Std.toFixed(6)})`,
          line: { color: '#ff9800', width: 2, dash: 'dash' }
        },
        {
          x: [p98, p98],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `P98 (${p98.toFixed(6)})`,
          line: { color: '#8e24aa', width: 2, dash: 'dot' }
        },
        {
          x: [scaledThreshold, scaledThreshold],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `Scaled threshold (${scaledThreshold.toFixed(6)})`,
          line: { color: '#ff6f00', width: 2, dash: 'dash' }
        },
        {
          x: [critical13Threshold, critical13Threshold],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `1.4x critical (${critical13Threshold.toFixed(6)})`,
          line: { color: '#d32f2f', width: 2, dash: 'dashdot' }
        }
      ];
    })()
    : [];
  const testMseHistogramPlot = (sensorData && ae?.status === 'ok' && ae?.test_scores?.length > 0)
    ? (() => {
      const trainScores = ae?.train_scores || [];
      const testScores = ae.test_scores;
      const refScores = trainScores.length > 1 ? trainScores : testScores;
      const refMean = refScores.reduce((a, b) => a + b, 0) / refScores.length;
      const refStd = refScores.length > 1
        ? Math.sqrt(refScores.reduce((a, b) => a + Math.pow(b - refMean, 2), 0) / (refScores.length - 1))
        : 0;
      const testMin = Math.min(...testScores);
      const testMax = Math.max(...testScores);
      const binWidth = Math.max((testMax - testMin) / 30, 1e-6);
      const xStart = testMin;
      const xEnd = Math.max(testMax, refMean + 4 * refStd);
      const meanPlus3Std = refMean + (3 * refStd);
      const refSorted = [...refScores].sort((a, b) => a - b);
      const p98 = refSorted[Math.max(0, Math.min(refSorted.length - 1, Math.floor(0.98 * refSorted.length) - 1))];
      const baseThreshold = Math.max(p98, meanPlus3Std);
      const thresholdScale = ae?.threshold_scale ?? 1.15;
      const scaledThreshold = baseThreshold * thresholdScale;
      const critical13Threshold = scaledThreshold * 1.4;
      const xVals = [];
      for (let x = xStart; x <= xEnd; x += binWidth) xVals.push(x);
      const gaussianCountScaled = xVals.map((x) => normalPdf(x, refMean, refStd) * testScores.length * binWidth);
      const yMax = Math.max(...gaussianCountScaled, 1);

      return [
        {
          x: testScores,
          type: 'histogram',
          name: 'Test MSE',
          marker: { color: '#d62728' },
          opacity: 0.8,
          autobinx: false,
          xbins: { start: xStart, end: xEnd, size: binWidth }
        },
        {
          x: xVals,
          y: gaussianCountScaled,
          type: 'scatter',
          mode: 'lines',
          name: 'Gaussian fit (light, train-based)',
          line: { color: 'rgba(46, 125, 50, 0.45)', width: 2 }
        },
        {
          x: [meanPlus3Std, meanPlus3Std],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `Mean + 3*Std (${meanPlus3Std.toFixed(6)})`,
          line: { color: '#ff9800', width: 2, dash: 'dash' }
        },
        {
          x: [p98, p98],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `P98 (train ref: ${p98.toFixed(6)})`,
          line: { color: '#8e24aa', width: 2, dash: 'dot' }
        },
        {
          x: [scaledThreshold, scaledThreshold],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `Scaled threshold (${scaledThreshold.toFixed(6)})`,
          line: { color: '#ff6f00', width: 2, dash: 'dash' }
        },
        {
          x: [critical13Threshold, critical13Threshold],
          y: [0, yMax],
          type: 'scatter',
          mode: 'lines',
          name: `1.4x critical (${critical13Threshold.toFixed(6)})`,
          line: { color: '#d32f2f', width: 2, dash: 'dashdot' }
        }
      ];
    })()
    : [];
  const averageWindowMsePlot = (sensorData && ae?.status === 'ok' && ae?.test_scores?.length > 0)
    ? (() => {
      const windowIndex = ae.test_scores.map((_, idx) => idx + 1);
      const avgMse = ae.test_scores.reduce((sum, v) => sum + Number(v), 0) / ae.test_scores.length;

      return [
        {
          x: windowIndex,
          y: ae.test_scores,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Window MSE',
          line: { color: '#1976d2', width: 2 },
          marker: { size: 4 }
        },
        {
          x: [1, windowIndex[windowIndex.length - 1]],
          y: [avgMse, avgMse],
          type: 'scatter',
          mode: 'lines',
          name: `Average MSE (${avgMse.toFixed(6)})`,
          line: { color: '#2e7d32', width: 3, dash: 'dash' }
        }
      ];
    })()
    : [];
  const averageTrainWindowMsePlot = (sensorData && ae?.status === 'ok' && ae?.train_scores?.length > 0)
    ? (() => {
      const windowIndex = ae.train_scores.map((_, idx) => idx + 1);
      const avgMse = ae.train_scores.reduce((sum, v) => sum + Number(v), 0) / ae.train_scores.length;

      return [
        {
          x: windowIndex,
          y: ae.train_scores,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Train Window MSE',
          line: { color: '#6a1b9a', width: 2 },
          marker: { size: 4 }
        },
        {
          x: [1, windowIndex[windowIndex.length - 1]],
          y: [avgMse, avgMse],
          type: 'scatter',
          mode: 'lines',
          name: `Train Average MSE (${avgMse.toFixed(6)})`,
          line: { color: '#00897b', width: 3, dash: 'dash' }
        }
      ];
    })()
    : [];
  const mainTrainWindowScores = Array.isArray(ae?.train_scores) ? ae.train_scores : [];
  const mainTestWindowScores = Array.isArray(ae?.test_scores) ? ae.test_scores : [];
  const mainAllWindowScores = [...mainTrainWindowScores, ...mainTestWindowScores]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
  const mainWindowMseHistogramConfig = (() => {
    if (mainAllWindowScores.length === 0) return null;
    const minScore = Math.min(...mainAllWindowScores);
    const maxScore = Math.max(...mainAllWindowScores);
    const binCount = 30;
    const epsilon = 1e-9;
    const span = Math.max(maxScore - minScore, epsilon);
    const binSize = span / binCount;
    return {
      start: minScore,
      end: maxScore + epsilon,
      size: binSize,
      range: [minScore, maxScore + epsilon]
    };
  })();
  const trainWindowMseHistogramPlot = (sensorData && ae?.status === 'ok' && ae?.train_scores?.length > 0)
    ? [
      {
        x: ae.train_scores,
        type: 'histogram',
        name: 'Train Window MSE',
        marker: { color: 'rgba(106, 27, 154, 0.75)' },
        opacity: 0.85,
        autobinx: false,
        xbins: mainWindowMseHistogramConfig
          ? {
            start: mainWindowMseHistogramConfig.start,
            end: mainWindowMseHistogramConfig.end,
            size: mainWindowMseHistogramConfig.size
          }
          : undefined
      }
    ]
    : [];
  const testWindowMseHistogramPlot = (sensorData && ae?.status === 'ok' && ae?.test_scores?.length > 0)
    ? [
      {
        x: ae.test_scores,
        type: 'histogram',
        name: 'Test Window MSE',
        marker: { color: 'rgba(25, 118, 210, 0.75)' },
        opacity: 0.85,
        autobinx: false,
        xbins: mainWindowMseHistogramConfig
          ? {
            start: mainWindowMseHistogramConfig.start,
            end: mainWindowMseHistogramConfig.end,
            size: mainWindowMseHistogramConfig.size
          }
          : undefined
      }
    ]
    : [];
  const thresholdMarkerPlot = (sensorData && ae?.status === 'ok' && ae?.test_scores?.length > 0)
    ? [
      {
        x: ae.test_timestamps,
        y: ae.test_scores,
        type: 'scatter',
        mode: 'lines',
        name: 'Test MSE',
        line: { color: '#1f77b4', width: 2 }
      },
      {
        x: ae.test_timestamps,
        y: (ae?.test_scores || []).map(() => ae?.base_threshold ?? ae?.threshold),
        type: 'scatter',
        mode: 'lines',
        name: `Base Threshold (${(ae?.base_threshold ?? ae?.threshold ?? 0).toFixed(6)})`,
        line: { color: '#2ca02c', width: 2, dash: 'dot' }
      },
      {
        x: ae.test_timestamps,
        y: (ae?.test_scores || []).map(() => ae?.threshold),
        type: 'scatter',
        mode: 'lines',
        name: `Alert Threshold (${(ae?.threshold ?? 0).toFixed(6)})`,
        line: { color: '#ff0000', width: 3, dash: 'dash' }
      }
    ]
    : [];
  const trainReconstructedSignalPlot = (sensorData && ae?.status === 'ok' && ae?.mode === 'global'
    && ae?.train_timestamps_aligned?.length > 0
    && ae?.train_reconstructed_strain?.length === ae?.train_timestamps_aligned?.length
    && ae?.train_original_strain_aligned?.length === ae?.train_timestamps_aligned?.length) ? [
    {
      x: ae.train_timestamps_aligned,
      y: ae.train_original_strain_aligned,
      type: 'scatter',
      mode: 'lines',
      name: 'Original Strain (Train)',
      line: { color: '#1f77b4', width: 2 }
    },
    {
      x: ae.train_timestamps_aligned,
      y: ae.train_reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: 'Reconstructed Strain (Train)',
      line: { color: '#2ca02c', width: 2, dash: 'dash' }
    }
  ] : [];
  const testReconstructedSignalPlot = (sensorData && ae?.status === 'ok' && ae?.mode === 'global'
    && ae?.test_timestamps?.length > 0
    && ae?.test_reconstructed_strain?.length === ae?.test_timestamps?.length
    && ae?.test_original_strain_aligned?.length === ae?.test_timestamps?.length) ? [
    {
      x: ae.test_timestamps,
      y: ae.test_original_strain_aligned,
      type: 'scatter',
      mode: 'lines',
      name: 'Original Strain (Test)',
      line: { color: '#1f77b4', width: 2 }
    },
    {
      x: ae.test_timestamps,
      y: ae.test_reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: 'Reconstructed Strain (Test)',
      line: { color: '#2ca02c', width: 2, dash: 'dash' }
    }
  ] : [];
  const estimatedSplit = sensorData?.strain_raw?.length
    ? Math.floor(sensorData.strain_raw.length * 0.8)
    : 0;
  const splitIndex = ae?.split_index ?? estimatedSplit;
  const trainInputExample = (sensorData?.strain_raw?.length >= 5 && splitIndex >= 5)
    ? sensorData.strain_raw.slice(Math.max(0, splitIndex - 5), splitIndex).map((v) => Number(v).toFixed(3)).join(', ')
    : 'N/A';
  const trainTempExample = (sensorData?.temperature?.length && splitIndex > 0)
    ? Number(sensorData.temperature[splitIndex - 1]).toFixed(3)
    : 'N/A';
  const testInputExample = (sensorData?.strain_raw?.length >= 5 && splitIndex + 5 <= sensorData.strain_raw.length)
    ? sensorData.strain_raw.slice(splitIndex, splitIndex + 5).map((v) => Number(v).toFixed(3)).join(', ')
    : 'N/A';
  const testTempExample = (sensorData?.temperature?.length > splitIndex)
    ? Number(sensorData.temperature[splitIndex]).toFixed(3)
    : 'N/A';

  return (
    <Box sx={{ p: 3, pt: '80px', maxWidth: 1400, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dataaaa Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a data file: B27 (MUX logger in training/), M16 (MUX in endpier1/), or dataaaa1 (Strain/TT in train 2/). MUX files are mapped automatically. Includes Strain vs Temperature,
          Original Strain vs Timestamp (test set only), Zeroed Strain vs Timestamp,
          and {modelLabel.toLowerCase()} reconstruction-error plots with compared windows
          ({SEQUENCE_LENGTH}, {COMPARISON_SEQUENCE_LENGTH}, {EXTENDED_COMPARISON_SEQUENCE_LENGTH}, and {LARGE_COMPARISON_SEQUENCE_LENGTH}
          {isLstm ? ' timesteps' : ' strain-history lengths'}).
          {!isLstm && (
            <> Feedforward 11/48-element runs use bottleneck <strong>{FEEDFORWARD_LARGE_BOTTLENECK}</strong> and <strong>{FEEDFORWARD_DEEP_LAYERS}</strong> encoder/decoder layers (seq 5/8 keep bottleneck 2, 2 layers).</>
          )}
          {isLstm && (
            <> LSTM encoder/decoder hidden bottleneck: <strong>{LSTM_BOTTLENECK}</strong> (all sequence lengths).</>
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} disabled={loading}>
            <InputLabel id="model-type-label">Model</InputLabel>
            <Select
              labelId="model-type-label"
              value={modelType}
              label="Model"
              onChange={(e) => {
                setModelType(e.target.value);
                setResults(null);
              }}
            >
              <MenuItem value="autoencoder">Feedforward</MenuItem>
              <MenuItem value="lstm">LSTM</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 320 }} disabled={loading || dataFiles.length === 0}>
            <InputLabel id="data-file-label">Data File</InputLabel>
            <Select
              labelId="data-file-label"
              value={selectedSource}
              label="Data File"
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              {dataFiles.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" onClick={loadData} disabled={loading || !selectedSource}>
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {loading ? 'Loading...' : 'Load data'}
          </Button>

          {results?.sensors?.length > 0 && (
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="sensor-select-label">Sensor</InputLabel>
              <Select
                labelId="sensor-select-label"
                value={selectedSensor}
                label="Sensor"
                onChange={(e) => setSelectedSensor(e.target.value)}
              >
                {results.sensors.map((s) => (
                  <MenuItem key={s.sensor} value={s.sensor}>
                    {s.sensor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="mode-select-label">Analysis Mode</InputLabel>
            <Select
              labelId="mode-select-label"
              value={analysisMode}
              label="Analysis Mode"
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue === 'compare-piers') {
                  navigate('/monitor/compare');
                  return;
                }
                setAnalysisMode(newValue);
              }}
            >
              <MenuItem value="global">Global</MenuItem>
              <MenuItem value="compare-piers">Compare Piers</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="view-select-label">Dataaaa View</InputLabel>
            <Select
              labelId="view-select-label"
              value={pageView}
              label="Dataaaa View"
              onChange={(e) => setPageView(e.target.value)}
            >
              <MenuItem value="analysis">Analysis</MenuItem>
              <MenuItem value="theory">Theory</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {sensorData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {sensorData.sensor} baseline used for zeroing: <strong>{sensorData.baseline.toFixed(4)}</strong>
          </Alert>
        )}
        {sensorData && ae?.status === 'ok' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {modelLabel} autoencoder (80/20)
            {ae.sequence_length != null ? `, seq=${ae.sequence_length}` : ''}: train={ae.train_points}, test={ae.test_points}, threshold={ae.threshold.toFixed(6)}
          </Alert>
        )}
        {sensorData && ae?.status === 'ok' && aeCompare?.status === 'ok' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Comparison ready: seq={ae.sequence_length ?? SEQUENCE_LENGTH} vs seq={aeCompare.sequence_length ?? COMPARISON_SEQUENCE_LENGTH}
            {' '}({windowDesc(aeCompare.sequence_length ?? COMPARISON_SEQUENCE_LENGTH)}), same 80/20 split.
          </Alert>
        )}
        {sensorData && sensorData?.autoencoder_80_20_compare_11?.status === 'ok' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Extended comparison ready: seq={sensorData.autoencoder_80_20_compare_11.sequence_length ?? EXTENDED_COMPARISON_SEQUENCE_LENGTH}
            {' '}({windowDesc(sensorData.autoencoder_80_20_compare_11.sequence_length ?? EXTENDED_COMPARISON_SEQUENCE_LENGTH)}).
          </Alert>
        )}
        {sensorData && sensorData?.autoencoder_80_20_compare_48?.status === 'ok' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Large-window comparison ready: seq={sensorData.autoencoder_80_20_compare_48.sequence_length ?? LARGE_COMPARISON_SEQUENCE_LENGTH}
            {' '}({windowDesc(sensorData.autoencoder_80_20_compare_48.sequence_length ?? LARGE_COMPARISON_SEQUENCE_LENGTH)}).
          </Alert>
        )}
        {sensorData && ae?.status === 'ok' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Thresholds for {sensorData.sensor} ({modelLabel}): seq {SEQUENCE_LENGTH} = <strong>{fmtThreshold(ae?.threshold)}</strong>
            {' '}| seq {compareSeq8} = <strong>{fmtThreshold(aeCompare?.threshold)}</strong>
            {' '}| seq {compareSeq11} = <strong>{fmtThreshold(aeCompare11?.threshold)}</strong>
            {' '}| seq {compareSeq48} = <strong>{fmtThreshold(aeCompare48?.threshold)}</strong>
          </Alert>
        )}
      </Paper>

      {sensorData && pageView === 'analysis' && (
        <>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Strain vs Temperature
            </Typography>
            <Plot
              data={strainVsTempPlot}
              layout={{
                title: `${sensorData.sensor} vs ${sensorData.temperature_sensor}`,
                xaxis: { title: `${sensorData.temperature_sensor} (Temperature)` },
                yaxis: { title: 'Strain (Original)' },
                height: 450,
                hovermode: 'closest',
                margin: { t: 60, b: 60, l: 70, r: 40 }
              }}
              style={{ width: '100%' }}
            />
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Input vs Feedforward &amp; LSTM Output (seq=5, test)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Not the full recording: only the 20% test split, one point per sliding window (seq=5).
              Green = input strain at each window; blue/orange = model output (reconstructed strain) for Feedforward and LSTM.
            </Typography>
            {hasCombinedOverlayPlot ? (
              <Plot
                data={combinedStrainReconstructionPlot}
                layout={{
                  title: `${sensorData.sensor} — Input vs Model Output (strain)`,
                  xaxis: { title: 'Timestamp' },
                  yaxis: {
                    title: 'Strain',
                    showgrid: true,
                  },
                  legend: { orientation: 'h', y: 1.12 },
                  height: 520,
                  hovermode: 'x unified',
                  margin: { t: 70, b: 60, l: 70, r: 40 },
                }}
                style={{ width: '100%' }}
                useResizeHandler
                config={{ responsive: true }}
              />
            ) : (
              <Alert severity="info">
                Needs input (test strain) plus Feedforward and LSTM seq=5 output on the test split.
                Reload data after both models finish training.
              </Alert>
            )}
            {(aeFeedforwardSeq5?.status !== 'ok' || aeLstmSeq5?.status !== 'ok') && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {aeFeedforwardSeq5?.status !== 'ok' && (
                  <> Feedforward seq=5: {aeFeedforwardSeq5?.reason || 'unavailable'}. </>
                )}
                {aeLstmSeq5?.status !== 'ok' && (
                  <> LSTM seq=5: {aeLstmSeq5?.reason || 'unavailable'}. </>
                )}
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Original Strain vs Timestamp (Test Set)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Original (uncalibrated) strain for the 20% test split only — aligned to model scoring windows, same as MOM.
            </Typography>
            {originalStrainVsTimestampPlot.length > 0 ? (
              <Plot
                data={originalStrainVsTimestampPlot}
                layout={{
                  title: `${sensorData.sensor} Original Strain vs Timestamp (Test)`,
                  xaxis: { title: 'Timestamp' },
                  yaxis: { title: 'Original Strain (Test)' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">Load data and run the 80/20 model to show test-set original strain.</Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Zeroed Strain vs Timestamp
            </Typography>
            <Plot
              data={zeroedStrainVsTimestampPlot}
              layout={{
                title: `${sensorData.sensor} Zeroed Strain vs Timestamp`,
                xaxis: { title: 'Timestamp' },
                yaxis: { title: 'Zeroed Strain' },
                height: 500,
                hovermode: 'closest',
                margin: { t: 60, b: 60, l: 70, r: 40 }
              }}
              style={{ width: '100%' }}
            />
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - {modelLabel} Reconstruction Error vs Timestamp (80/20, seq={ae?.sequence_length ?? SEQUENCE_LENGTH})
            </Typography>
            {ae?.status === 'ok' ? (
              <Plot
                data={autoencoderPlot}
                layout={{
                  title: `${sensorData.sensor} ${modelLabel} Reconstruction Error (seq=${ae?.sequence_length ?? SEQUENCE_LENGTH})`,
                  xaxis: { title: 'Timestamp (Test 20%)' },
                  yaxis: { title: 'Reconstruction Error (MSE)' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity={ae?.status === 'error' ? 'error' : 'warning'}>
                {ae?.reason || `${modelLabel} plot unavailable for this sensor.`}
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - {modelLabel} Reconstruction Error (seq={compareSeq8})
            </Typography>
            {autoencoderPlot8.length > 0 ? (
              <Plot
                data={autoencoderPlot8}
                layout={{
                  title: `${sensorData.sensor} ${modelLabel} Reconstruction Error (seq=${compareSeq8}, ${windowDesc(compareSeq8)})`,
                  xaxis: { title: 'Timestamp (Test 20%)' },
                  yaxis: { title: 'Reconstruction Error (MSE)' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                seq={compareSeq8} plot appears after that window run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - {modelLabel} Reconstruction Error (seq={compareSeq11})
            </Typography>
            {autoencoderPlot11.length > 0 ? (
              <Plot
                data={autoencoderPlot11}
                layout={{
                  title: `${sensorData.sensor} ${modelLabel} Reconstruction Error (seq=${compareSeq11}, ${windowDesc(compareSeq11)})`,
                  xaxis: { title: 'Timestamp (Test 20%)' },
                  yaxis: { title: 'Reconstruction Error (MSE)' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                seq={compareSeq11} plot appears after that window run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - {modelLabel} Reconstruction Error (seq={compareSeq48})
            </Typography>
            {autoencoderPlot48.length > 0 ? (
              <Plot
                data={autoencoderPlot48}
                layout={{
                  title: `${sensorData.sensor} ${modelLabel} Reconstruction Error (seq=${compareSeq48}, ${windowDesc(compareSeq48)})`,
                  xaxis: { title: 'Timestamp (Test 20%)' },
                  yaxis: { title: 'Reconstruction Error (MSE)' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                seq={compareSeq48} plot appears after that window run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Original vs Reconstructed Signal (Test)
            </Typography>
            {reconstructedSignalPlot.length > 0 ? (
              <Plot
                data={reconstructedSignalPlot}
                layout={{
                  title: `${sensorData.sensor} Test Signal Reconstruction`,
                  xaxis: { title: 'Timestamp (Test 20%)' },
                  yaxis: { title: 'Strain Value' },
                  height: 500,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                Reconstructed signal plot is currently available in global mode (80/20 split).
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Main Model Average MSE Across Train Windows
            </Typography>
            {averageTrainWindowMsePlot.length > 0 ? (
              <Plot
                data={averageTrainWindowMsePlot}
                layout={{
                  title: `${sensorData.sensor} Train Window MSE (Main Model)`,
                  xaxis: { title: 'Window Index (Train)' },
                  yaxis: { title: 'MSE' },
                  height: 450,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                Train window-level MSE plot appears after the main model run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Main Model Train Window MSE Histogram
            </Typography>
            {trainWindowMseHistogramPlot.length > 0 ? (
              <Plot
                data={trainWindowMseHistogramPlot}
                layout={{
                  barmode: 'overlay',
                  title: `${sensorData.sensor} Train Window MSE Distribution (Main Model)`,
                  xaxis: {
                    title: 'Window Mean MSE (Train)',
                    range: mainWindowMseHistogramConfig?.range
                  },
                  yaxis: { title: 'Window Count' },
                  height: 430,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                Train window-MSE histogram appears after the main model run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Main Model Average MSE Across Windows
            </Typography>
            {averageWindowMsePlot.length > 0 ? (
              <Plot
                data={averageWindowMsePlot}
                layout={{
                  title: `${sensorData.sensor} Test Window MSE (Main Model)`,
                  xaxis: { title: 'Window Index (Test)' },
                  yaxis: { title: 'MSE' },
                  height: 450,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                Window-level MSE plot appears after the main model run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Main Model Test Window MSE Histogram
            </Typography>
            {testWindowMseHistogramPlot.length > 0 ? (
              <Plot
                data={testWindowMseHistogramPlot}
                layout={{
                  barmode: 'overlay',
                  title: `${sensorData.sensor} Test Window MSE Distribution (Main Model)`,
                  xaxis: {
                    title: 'Window Mean MSE (Test)',
                    range: mainWindowMseHistogramConfig?.range
                  },
                  yaxis: { title: 'Window Count' },
                  height: 430,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">
                Test window-MSE histogram appears after the main model run succeeds for this sensor.
              </Alert>
            )}
          </Paper>

          {ae?.status === 'ok' && (
            <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {sensorData.sensor} - Model Evaluation
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Mode: <strong>{ae.mode || 'global'}</strong>
                {' '}| Model: <strong>{ae.model_type || results?.model_type || modelType}</strong>
                {' '}| seq: <strong>{ae.sequence_length ?? SEQUENCE_LENGTH}</strong>
                {ae?.bottleneck_dim != null && (
                  <> | bottleneck: <strong>{ae.bottleneck_dim}</strong>
                    {!isLstm && ae?.num_layers != null && <> | layers: <strong>{ae.num_layers}</strong></>}
                    {ae?.hidden_dim != null && !isLstm && <> (hidden {ae.hidden_dim})</>}</>
                )}
              </Typography>
              <Typography variant="body2">Train points: <strong>{ae.train_points}</strong></Typography>
              <Typography variant="body2">Test points: <strong>{ae.test_points}</strong></Typography>
              <Typography variant="body2">
                Base threshold (train): <strong>{baseThresholdValue != null ? baseThresholdValue.toFixed(6) : 'N/A'}</strong>
                {' '}| implied k: <strong>{baseImpliedK != null ? baseImpliedK.toFixed(2) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Warning threshold (scaled): <strong>{warningThresholdValue != null ? warningThresholdValue.toFixed(6) : 'N/A'}</strong>
                {' '}| implied k: <strong>{warningImpliedK != null ? warningImpliedK.toFixed(2) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Critical threshold: <strong>{criticalThresholdValue != null ? criticalThresholdValue.toFixed(6) : 'N/A'}</strong>
                {' '}| implied k: <strong>{criticalImpliedK != null ? criticalImpliedK.toFixed(2) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Anomalies: <strong>{anomalyCount}</strong> / <strong>{scoreCount}</strong>
                {' '}(<strong>{anomalyRate.toFixed(2)}%</strong>)
              </Typography>
              <Typography variant="body2">
                Anomaly rule: score &gt; <strong>model training threshold</strong> (from fit(); same rule for Feedforward and LSTM).
              </Typography>
              <Typography variant="body2">
                Score mean: <strong>{scoreMean != null ? scoreMean.toFixed(6) : 'N/A'}</strong>
                {' '}| std: <strong>{scoreStd != null ? scoreStd.toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Score min/max: <strong>{scoreMin != null ? scoreMin.toFixed(6) : 'N/A'}</strong>
                {' '}/ <strong>{scoreMax != null ? scoreMax.toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Train: first-window first-element reconstruction error:{' '}
                <strong>{ae?.first_train_window_first_element_recon_error != null ? Number(ae.first_train_window_first_element_recon_error).toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Train: first-window mean MSE (after all epochs):{' '}
                <strong>{ae?.first_train_window_mean_mse != null ? Number(ae.first_train_window_mean_mse).toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Test: first-window first-element reconstruction error:{' '}
                <strong>{ae?.first_window_first_element_recon_error != null ? Number(ae.first_window_first_element_recon_error).toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Test: first-window mean MSE (after all epochs):{' '}
                <strong>{ae?.first_window_mean_mse != null ? Number(ae.first_window_mean_mse).toFixed(6) : 'N/A'}</strong>
              </Typography>
              {aeCompare48?.status === 'ok' && (
                <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>seq={compareSeq48} diagnostics</strong>
                  </Typography>
                  <Typography variant="body2">
                    48E Train: first-window first-element reconstruction error:{' '}
                    <strong>{aeCompare48?.first_train_window_first_element_recon_error != null ? Number(aeCompare48.first_train_window_first_element_recon_error).toFixed(6) : 'N/A'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    48E Train: first-window mean MSE (after all epochs):{' '}
                    <strong>{aeCompare48?.first_train_window_mean_mse != null ? Number(aeCompare48.first_train_window_mean_mse).toFixed(6) : 'N/A'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    48E Test: first-window first-element reconstruction error:{' '}
                    <strong>{aeCompare48?.first_window_first_element_recon_error != null ? Number(aeCompare48.first_window_first_element_recon_error).toFixed(6) : 'N/A'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    48E Test: first-window mean MSE (after all epochs):{' '}
                    <strong>{aeCompare48?.first_window_mean_mse != null ? Number(aeCompare48.first_window_mean_mse).toFixed(6) : 'N/A'}</strong>
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Train anomaly rate: <strong>{((evalMetrics.train_anomaly_rate || 0) * 100).toFixed(2)}%</strong>
                {' '}| Test anomaly rate: <strong>{((evalMetrics.test_anomaly_rate || 0) * 100).toFixed(2)}%</strong>
              </Typography>
              {ae.mode === 'global' && (
                <Typography variant="body2">
                  Warning rate (scaled threshold): <strong>{((evalMetrics.test_warning_rate || 0) * 100).toFixed(2)}%</strong>
                  {' '}| threshold scale: <strong>{ae.threshold_scale != null ? ae.threshold_scale.toFixed(2) : '1.15'}</strong>
                </Typography>
              )}
              {ae.mode === 'global' && (
                <Typography variant="body2">
                  Persistent test anomaly rate: <strong>{((evalMetrics.test_anomaly_rate_persistent || 0) * 100).toFixed(2)}%</strong>
                  {' '} (k=<strong>{evalMetrics.persistence_k ?? 3}</strong>, n=<strong>{evalMetrics.persistence_n ?? 5}</strong>)
                </Typography>
              )}
              <Typography variant="body2">
                Train score mean/std: <strong>{trainStats.mean != null ? trainStats.mean.toFixed(6) : 'N/A'}</strong>
                {' '} / <strong>{trainStats.std != null ? trainStats.std.toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                μ_train_mse: <strong>{trainStats.mean != null ? trainStats.mean.toFixed(6) : 'N/A'}</strong>
                {' '}| σ_train_mse: <strong>{trainStats.std != null ? trainStats.std.toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Test score mean/std: <strong>{testStats.mean != null ? testStats.mean.toFixed(6) : 'N/A'}</strong>
                {' '} / <strong>{testStats.std != null ? testStats.std.toFixed(6) : 'N/A'}</strong>
              </Typography>
              <Typography variant="body2">
                Separation (sigma): <strong>{evalMetrics.separation_sigma != null ? evalMetrics.separation_sigma.toFixed(3) : 'N/A'}</strong>
              </Typography>
              {ae.mode === 'global' && (
                <Typography variant="body2">
                  Test rate first half: <strong>{((evalMetrics.test_anomaly_rate_first_half || 0) * 100).toFixed(2)}%</strong>
                  {' '}| second half: <strong>{((evalMetrics.test_anomaly_rate_second_half || 0) * 100).toFixed(2)}%</strong>
                  {' '}| delta: <strong>{((evalMetrics.test_anomaly_rate_delta || 0) * 100).toFixed(2)}%</strong>
                </Typography>
              )}
            </Paper>
          )}

          {ae?.status === 'ok' && (
            <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {sensorData.sensor} - Warning / Critical Points by Element Window
              </Typography>
              {anomalyPointGroups.map((group) => (
                <Box key={group.key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {group.title}
                  </Typography>
                  {group.model?.status !== 'ok' ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Model run unavailable for this window size.
                    </Alert>
                  ) : group.points.length > 0 ? (
                    <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                      {group.points.map((pt, idx) => {
                        const severityColor = pt.severity === 'critical' ? '#d62728' : '#fbc02d';
                        const testRecord = group.model?.test_record_numbers?.[pt.sourceIndex];
                        const strainAtPoint = sensorData?.strain_raw?.[sensorData.timestamps.indexOf(pt.timestamp)];
                        const tempAtPoint = sensorData?.temperature?.[sensorData.timestamps.indexOf(pt.timestamp)];
                        return (
                          <Typography key={`${group.key}-${pt.timestamp}-${idx}`} variant="body2" sx={{ mb: 0.8 }}>
                            {idx + 1}. Time: <strong>{pt.timestamp}</strong>
                            {' '}| Record: <strong>{testRecord != null ? Number(testRecord).toFixed(0) : 'N/A'}</strong>
                            {' '}| Strain: <strong>{strainAtPoint != null ? Number(strainAtPoint).toFixed(4) : 'N/A'}</strong>
                            {' '}| Temp: <strong>{tempAtPoint != null ? Number(tempAtPoint).toFixed(4) : 'N/A'}</strong>
                            {' '}| MSE: <strong>{pt.score.toFixed(6)}</strong>
                            {' '}| Threshold: <strong>{pt.threshold != null ? Number(pt.threshold).toFixed(6) : 'N/A'}</strong>
                            {' '}| Severity: <strong style={{ color: severityColor }}>{pt.severity.toUpperCase()}</strong>
                          </Typography>
                        );
                      })}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      No warning/critical points detected for this window size.
                    </Alert>
                  )}
                </Box>
              ))}
            </Paper>
          )}
        </>
      )}

      {sensorData && pageView === 'theory' && (
        <>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Strain Autocorrelation (Lag Analysis)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This shows how much current strain stays correlated with past readings as lag increases.
              Lag 0 is always 1.0; lower values mean weaker memory of past values.
            </Typography>
            {autocorrelationPlot.length > 0 ? (
              <>
                <Plot
                  data={autocorrelationPlot}
                  layout={{
                    title: `${sensorData.sensor} Autocorrelation vs Lag`,
                    xaxis: { title: 'Lag (samples)' },
                    yaxis: { title: 'Autocorrelation r(lag)', range: [-1, 1] },
                    height: 440,
                    hovermode: 'closest',
                    margin: { t: 60, b: 60, l: 70, r: 40 }
                  }}
                  style={{ width: '100%' }}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Correlation decay: r&lt;0.5 at{' '}
                  <strong>{lag05 ? `lag ${lag05.lag}` : 'not reached in shown lags'}</strong>
                  {lag05?.approxHours != null ? ` (~${lag05.approxHours.toFixed(2)} hours)` : ''},
                  {' '}r&lt;0.2 at <strong>{lag02 ? `lag ${lag02.lag}` : 'not reached'}</strong>
                  {lag02?.approxHours != null ? ` (~${lag02.approxHours.toFixed(2)} hours)` : ''},
                  {' '}r&lt;0 at <strong>{lag0 ? `lag ${lag0.lag}` : 'not reached'}</strong>
                  {lag0?.approxHours != null ? ` (~${lag0.approxHours.toFixed(2)} hours)` : ''}.
                </Typography>
              </>
            ) : (
              <Alert severity="info">
                Not enough valid strain points to compute autocorrelation for this sensor.
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Train MSE Histogram
            </Typography>
            {trainMseHistogramPlot.length > 0 ? (
              <Plot
                data={trainMseHistogramPlot}
                layout={{
                  barmode: 'overlay',
                  title: `${sensorData.sensor} Train MSE Distribution`,
                  xaxis: { title: 'MSE Score' },
                  yaxis: { title: 'Count / scaled density' },
                  height: 430,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">Load analysis first to see train MSE histogram.</Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Test MSE Histogram
            </Typography>
            {testMseHistogramPlot.length > 0 ? (
              <Plot
                data={testMseHistogramPlot}
                layout={{
                  barmode: 'overlay',
                  title: `${sensorData.sensor} Test MSE Distribution`,
                  xaxis: { title: 'MSE Score' },
                  yaxis: { title: 'Count / scaled density' },
                  height: 430,
                  hovermode: 'closest',
                  margin: { t: 60, b: 60, l: 70, r: 40 }
                }}
                style={{ width: '100%' }}
              />
            ) : (
              <Alert severity="info">Load analysis first to see test MSE histogram.</Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Critical Points
            </Typography>
            {criticalPoints.length > 0 ? (
              <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {criticalPoints.map((pt, idx) => (
                  <Typography key={`${pt.timestamp}-critical-${idx}`} variant="body2" sx={{ mb: 0.8 }}>
                    {idx + 1}. Time: <strong>{pt.timestamp}</strong>
                    {' '}| Strain: <strong>{pt.strain != null ? Number(pt.strain).toFixed(4) : 'N/A'}</strong>
                    {' '}| MSE: <strong>{pt.score.toFixed(6)}</strong>
                  </Typography>
                ))}
              </Box>
            ) : (
              <Alert severity="info">No critical points found.</Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} - Anomaly Points Beyond Threshold
            </Typography>
            {anomalyBeyondThresholdPoints.length > 0 ? (
              <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {anomalyBeyondThresholdPoints.map((pt, idx) => (
                  <Typography key={`${pt.timestamp}-beyond-${idx}`} variant="body2" sx={{ mb: 0.8 }}>
                    {idx + 1}. Time: <strong>{pt.timestamp}</strong>
                    {' '}| Strain: <strong>{pt.strain != null ? Number(pt.strain).toFixed(4) : 'N/A'}</strong>
                    {' '}| MSE: <strong>{pt.score.toFixed(6)}</strong>
                    {' '}| Threshold: <strong>{pt.threshold != null ? Number(pt.threshold).toFixed(6) : 'N/A'}</strong>
                  </Typography>
                ))}
              </Box>
            ) : (
              <Alert severity="info">No points beyond threshold found.</Alert>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default DataaaaAnalysis;

